import { Link } from "@tanstack/react-router"
import { Eye, EyeOff } from "lucide-react"
import { useMemo, useState } from "react"
import { ErrorState } from "@/components/states"
import { Skeleton } from "@/components/ui/skeleton"
import { useT } from "@/lib/i18n"
import { useObjects, usePrincipalRecord } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { cn } from "@/lib/utils"
import { useOntology } from "@/workspace/hooks"
import type { Ontology, TypeMeta } from "@/workspace/ontology"
import { RefusalNote } from "@/workspace/values"
import { dataAreas } from "./areas"
import { DataFrame } from "./frame"

export function PolicyPage() {
  const t = useT()
  const scope = useScope()
  const { ontology, defs } = useOntology(scope)
  const principal = usePrincipalRecord(scope)
  const [areaId, setAreaId] = useState<string | null>(null)
  const areas = useMemo(() => (ontology ? dataAreas(ontology) : []), [ontology])
  const area = areas.find((a) => a.id === areaId) ?? areas.find((a) => !a.core) ?? areas[0]
  const policies = defs.data?.artifacts.filter((a) => a.kind === "policy") ?? []
  return (
    <DataFrame title={t("dv.nav.policy")} description={t("dv.policy.description")} how={["dv.how.policy", "ws.how.restricted"]} crumbs={[t("dv.nav.policy")]}>
      {principal.isError ? (
        <RefusalNote error={principal.error} title={t("dv.policy.principalUnavailable")} />
      ) : principal.isPending ? (
        <Skeleton className="h-16 w-full rounded-2xl" />
      ) : (
        <dl className="grid grid-cols-2 gap-x-8 gap-y-4 border-y border-border py-5 sm:grid-cols-4">
          <div>
            <dt className="text-xs text-muted-foreground">{t("dv.policy.principal")}</dt>
            <dd className="text-lg font-semibold">{principal.data.actor}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">{t("dv.policy.roles")}</dt>
            <dd className="text-sm">{principal.data.roles.length ? principal.data.roles.join(", ") : "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">{t("dv.policy.units")}</dt>
            <dd className="text-sm">{principal.data.businessUnits.length ? principal.data.businessUnits.join(", ") : "—"}</dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">{t("dv.policy.policies")}</dt>
            <dd className="flex flex-wrap gap-1 text-xs">
              {policies.map((p) => (
                <Link key={p.artifactId} to="/$orgId/$environmentId/definitions/$kind/$artifactId" params={{ ...scope, kind: "policy", artifactId: p.artifactId }} dir="ltr" className="font-mono hover:underline">
                  {p.artifactId}
                </Link>
              ))}
            </dd>
          </div>
        </dl>
      )}
      <p className="max-w-3xl text-sm text-muted-foreground">{t("dv.policy.explain")}</p>
      {defs.isError ? (
        <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
      ) : !ontology || !area ? (
        <Skeleton className="h-72 w-full rounded-2xl" />
      ) : (
        <>
          <div role="group" aria-label={t("ws.nav.businessAreas")} className="flex flex-wrap gap-1.5">
            {areas.map((a) => (
              <button
                key={a.id}
                type="button"
                aria-pressed={a.id === area.id}
                onClick={() => setAreaId(a.id)}
                className={cn(
                  "h-8 rounded-full border px-3 text-xs outline-none transition-colors focus-visible:ring-3 focus-visible:ring-ring/30",
                  a.id === area.id ? "border-foreground/40 bg-accent font-medium" : "border-border text-muted-foreground hover:text-foreground",
                )}
              >
                {a.label}
              </button>
            ))}
          </div>
          <AreaAccess key={area.id} types={area.types.map((n) => ontology.types.get(n)).filter((x): x is TypeMeta => Boolean(x))} ontology={ontology} />
          <p className="text-xs text-muted-foreground">
            {t("dv.policy.more")}{" "}
            <Link to="/$orgId/$environmentId/permissions/rules" params={scope} search={{}} className="underline underline-offset-4 hover:text-foreground">
              {t("nav.permissions")}
            </Link>
          </p>
        </>
      )}
    </DataFrame>
  )
}

/** Per type: can this principal read it, and which fields does the cell withhold, with their markings. */
function AreaAccess({ types, ontology }: { types: TypeMeta[]; ontology: Ontology }) {
  const t = useT()
  const sorted = useMemo(() => [...types].sort((a, b) => a.label.localeCompare(b.label)).slice(0, 24), [types])
  return (
    <div className="overflow-x-auto rounded-2xl border border-border">
      <table className="w-full min-w-[720px] text-sm">
        <thead className="text-[11px] text-muted-foreground uppercase tracking-[0.06em]">
          <tr className="border-b border-border">
            {[t("dv.policy.type"), t("dv.policy.accessSampled"), t("dv.policy.withheld"), t("dv.policy.declared")].map((h) => (
              <th key={h} className="px-4 py-2.5 text-start font-medium">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sorted.map((type) => (
            <AccessRow key={type.name} type={type} ontology={ontology} />
          ))}
        </tbody>
      </table>
    </div>
  )
}

function AccessRow({ type }: { type: TypeMeta; ontology: Ontology }) {
  const t = useT()
  const scope = useScope()
  // One authorized record shows what this principal sees: readable, masked fields, or a refusal.
  const sample = useObjects(scope, type.name, null, 1)
  const record = sample.data?.items[0]
  const masked = Object.keys((record?.masked as Record<string, unknown> | undefined) ?? {})
  const declared = type.fields.filter((f) => f.markings.length)
  return (
    <tr className="border-b border-border/60 align-top last:border-0">
      <td className="px-4 py-2.5">
        <Link to="/$orgId/$environmentId/data/ontology" params={scope} search={{ type: type.name }} className="hover:underline">
          {type.label}
        </Link>
      </td>
      <td className="px-4 py-2.5">
        {sample.isPending ? (
          <Skeleton className="h-4 w-20" />
        ) : sample.isError ? (
          <RefusalNote error={sample.error} title={t("dv.policy.refused")} className="px-2 py-1.5 text-xs" />
        ) : record ? (
          <span className="inline-flex items-center gap-1.5 text-xs text-[var(--success)]">
            <Eye className="size-3.5" aria-hidden /> {masked.length ? t("dv.policy.readMasked") : t("dv.policy.read")}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">{t("dv.policy.none")}</span>
        )}
      </td>
      <td className="px-4 py-2.5">
        <span className="flex flex-wrap gap-1">
          {masked.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 rounded-full border border-dashed border-border px-2 py-0.5 text-2xs text-muted-foreground">
              <EyeOff className="size-3" aria-hidden />
              {type.fields.find((f) => f.id === id)?.label ?? id}
            </span>
          ))}
        </span>
      </td>
      <td className="px-4 py-2.5">
        <span className="flex flex-wrap gap-1">
          {declared.map((f) => (
            <span key={f.id} className="rounded-full border border-border px-2 py-0.5 text-2xs text-muted-foreground" title={f.markings.join(", ")}>
              {f.label} · <span dir="ltr" className="font-mono">{f.markings.join(", ")}</span>
            </span>
          ))}
        </span>
      </td>
    </tr>
  )
}
